/**
 * Dashboard Component
 *
 * Main dashboard providing navigation to Trading Hub, Analysis Hub, and Settings.
 * Uses full viewport card pattern for consistent mobile responsiveness.
 * Serves as the primary entry point for configured users.
 *
 * Session Modal Flow:
 * - Trading Hub card opens SessionModal for session selection
 * - User selects session type/date or resumes existing draft
 * - On selection, navigates to /trading/:draftId
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Settings } from 'lucide-react';

// 2. Workspace packages
import { Card, CardHeader, CardBody, CardFooter, ScrollShadow, Image, useToastNotification } from '@enfomentis/ui';
import { Brand } from '@enfomentis/shared/config';

// 3. Internal absolute imports
import { AppLayout } from '@/components/shared/layouts';
import { AppSyncStatus } from '@/components/shared/sync';
import { useAuth } from '@/hooks/useAuth';
import { userService } from '@/services/UserService';
import { db } from '@/lib/db';
import { getTodayInTimezone, getBrowserTimezone } from '@/utils/dateHelpers';
import { isTombstoned } from '@/utils/tombstone';
import { sessionDraftService } from '@/services/SessionDraftService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import TradingHubIllustrationPng from '@/assets/icons/TradingHubIllustration.png';
import AnalysisHubIllustrationPng from '@/assets/icons/AnalysisHubIllustration.png';

// 3. Type imports
import type { SessionDraft } from '@/types/transient/SessionDraft';

// 4. Relative imports
import { OnboardingReminderModal } from '@/components/Onboarding/modals/OnboardingReminderModal';
import { SessionModal } from '@/components/Dashboard/SessionModal';

/**
 * Main Dashboard Component
 * 
 * Provides navigation to the two primary application hubs:
 * - Trading Hub: Daily trading activities and session management
 * - Analysis Hub: Performance review and trading insights
 * - Settings: Application configuration
 * 
 * Features:
 * - Full viewport card layout for mobile responsiveness
 * - Persistent header and footer for app shell pattern
 * - Responsive grid layout for hub cards
 * - Professional card-based design with hover effects
 */
// Type for route state passed from PreTradingPhase redirect
interface DashboardRouteState {
  openSessionModal?: boolean;
  reason?: 'session-not-found' | 'session-deleted';
}

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showToast } = useToastNotification();

  // Onboarding reminder state
  const [showOnboardingReminder, setShowOnboardingReminder] = useState(false);
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(true);

  // Session modal state
  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const [drafts, setDrafts] = useState<SessionDraft[]>([]);
  // Initialize with browser timezone to prevent empty string errors
  const [displayTimezone, setDisplayTimezone] = useState<string>(() => getBrowserTimezone());
  const [todayDateString, setTodayDateString] = useState<string>(() => getTodayInTimezone(getBrowserTimezone()));

  // Mounted ref for user-initiated async calls (React 18 StrictMode compatible)
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true; // Reset on (re)mount for StrictMode compatibility
    return () => { isMountedRef.current = false; };
  }, []);

  // Ref to prevent duplicate route state handling in StrictMode
  const handledRouteStateRef = useRef(false);

  // Load session drafts and settings from IndexedDB
  const loadSessionData = useCallback(async (signal?: { aborted: boolean }): Promise<void> => {
    try {
      // Load settings and drafts in parallel for performance
      const [settings, allDrafts] = await Promise.all([
        db.basic_settings.orderBy('updatedAt').last().catch((err) => {
          console.error('[Dashboard] Failed to load settings:', err);
          return undefined;
        }),
        db.session_drafts.toArray().catch((err) => {
          console.error('[Dashboard] Failed to load drafts:', err);
          return [] as SessionDraft[];
        })
      ]);

      // Skip state updates if effect was cleaned up (React 18 StrictMode safe)
      if (signal?.aborted) return;
      // For user-initiated calls (no signal), check component-level mounted ref
      if (!signal && !isMountedRef.current) return;

      // Extract timezone with fallback to browser timezone
      const timezone = settings?.displayTimezone ?? getBrowserTimezone();
      const today = getTodayInTimezone(timezone);

      // Filter out tombstoned (soft-deleted) drafts
      const activeDrafts = allDrafts.filter(d => !isTombstoned(d));

      // Update state
      setDisplayTimezone(timezone);
      setTodayDateString(today);
      setDrafts(activeDrafts);
    } catch (error) {
      if (signal?.aborted) return;
      console.error('Failed to load session data:', error);
      // Fallback: use browser timezone and empty drafts
      const fallbackTimezone = getBrowserTimezone();
      setDisplayTimezone(fallbackTimezone);
      setTodayDateString(getTodayInTimezone(fallbackTimezone));
      setDrafts([]);
    }
  }, []);

  // Load session data on mount (React 18 StrictMode safe)
  useEffect(() => {
    const signal = { aborted: false };
    loadSessionData(signal);
    return () => { signal.aborted = true; };
  }, [loadSessionData]);

  // Lightweight drafts-only refresh for cross-tab updates (avoids settings churn)
  const refreshDrafts = useCallback(async (): Promise<void> => {
    try {
      const allDrafts = await db.session_drafts.toArray();

      // Unmount safety: check component-level mounted ref before state update
      if (!isMountedRef.current) return;

      // Filter out tombstoned (soft-deleted) drafts
      const activeDrafts = allDrafts.filter(d => !isTombstoned(d));
      setDrafts(activeDrafts);
    } catch (error) {
      console.error('[Dashboard] Failed to refresh drafts:', error);
      // Don't clear drafts on error - keep existing state
    }
  }, []);

  // Subscribe to cross-tab session_drafts changes (only refresh when modal is open)
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('session_drafts', () => {
      // Only refresh when modal is visible - user can see the changes
      // When modal opens, handleOpenSessionModal already calls loadSessionData()
      if (isSessionModalOpen) {
        refreshDrafts();
      }
    });

    return unsubscribe;
  }, [refreshDrafts, isSessionModalOpen]);

  // Handle route state from PreTradingPhase redirect (auto-open modal, show toast)
  useEffect(() => {
    const state = location.state as DashboardRouteState | null;
    if (!state) {
      // Reset ref when no state, so future redirects in the same session work
      handledRouteStateRef.current = false;
      return;
    }

    // Only handle our specific state properties
    const hasOurState = state.openSessionModal || state.reason;
    if (!hasOurState) {
      // Reset ref when state doesn't contain our properties
      handledRouteStateRef.current = false;
      return;
    }

    // Prevent duplicate handling in StrictMode
    if (handledRouteStateRef.current) return;
    handledRouteStateRef.current = true;

    // Auto-open session modal if requested
    // Refresh drafts first - Dashboard may already be mounted with stale data
    if (state.openSessionModal) {
      (async () => {
        await refreshDrafts();
        setIsSessionModalOpen(true);
      })();
    }

    // Show toast notification based on reason
    if (state.reason === 'session-not-found') {
      showToast({
        title: 'Session not found',
        description: 'Please select or create a session',
        color: 'warning',
      });
    } else if (state.reason === 'session-deleted') {
      showToast({
        title: 'Session was deleted',
        description: 'Please select or create a session',
        color: 'warning',
      });
    }

    // Clear route state to prevent re-opening on refresh
    // Only clear when we've handled our state (preserves other flows' state)
    // Preserve pathname/search/hash in case Dashboard ever uses query params
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: null }
    );
  }, [location, navigate, showToast, refreshDrafts]);

  // Check if user needs onboarding reminder
  useEffect(() => {
    const checkOnboardingStatus = async (): Promise<void> => {
      if (!user) {
        setIsCheckingOnboarding(false);
        return;
      }

      try {
        // Check if user has dismissed the reminder
        const dismissed = localStorage.getItem(`${Brand.cachePrefix}-onboarding-reminder-dismissed`);
        if (dismissed === 'true') {
          setIsCheckingOnboarding(false);
          return;
        }

        // Fetch user data to check onboarding status
        const userData = await userService.get(user.id);

        if (userData && !userData.onboardingCompleted) {
          console.log('📋 Dashboard: User has not completed onboarding, showing reminder');
          setShowOnboardingReminder(true);
        }
      } catch (error) {
        console.error('❌ Dashboard: Error checking onboarding status:', error);
      } finally {
        setIsCheckingOnboarding(false);
      }
    };

    checkOnboardingStatus();
  }, [user]);

  /** Open session modal - refreshes draft data to catch cross-tab updates */
  const handleOpenSessionModal = async (): Promise<void> => {
    await refreshDrafts();
    setIsSessionModalOpen(true);
  };

  /** Close session modal */
  const handleCloseSessionModal = (): void => {
    setIsSessionModalOpen(false);
  };

  /** Create new session draft and navigate to TradingHub */
  const handleStartNew = async (sessionType: SessionDraft['sessionType'], date: string): Promise<void> => {
    try {
      const newDraft = await sessionDraftService.create(sessionType, date);
      setIsSessionModalOpen(false);
      navigate(`/trading/${newDraft.id}`);
    } catch (error) {
      console.error('Failed to create session draft:', error);
      showToast({
        title: 'Failed to create session',
        description: 'Please try again',
        color: 'danger',
      });
    }
  };

  /** Resume existing session draft and navigate to TradingHub */
  const handleResume = (draftId: string): void => {
    setIsSessionModalOpen(false);
    navigate(`/trading/${draftId}`);
  };

  /** Soft delete session draft and update local state */
  const handleDelete = async (draftId: string): Promise<void> => {
    try {
      const wasDeleted = await sessionDraftService.softDelete(draftId);

      // Only update local state if deletion actually occurred
      if (wasDeleted) {
        setDrafts(prev => prev.filter(d => d.id !== draftId));
      }
    } catch (error) {
      console.error('Failed to delete session draft:', error);
      showToast({
        title: 'Failed to delete session',
        description: 'Please try again',
        color: 'danger',
      });
    }
  };

  const handleNavigateToAnalysisHub = (): void => {
    navigate('/analysis');
  };

  const handleNavigateToSettings = (): void => {
    navigate('/settings');
  };

  const handleCloseOnboardingReminder = (): void => {
    setShowOnboardingReminder(false);
  };

  return (
    <AppLayout headerContent={<AppSyncStatus />}>
      <div className="flex-1 p-0 min-h-0 ">
          <ScrollShadow 
            className="h-full p-6"
            hideScrollBar
            size={20}
          >
            <div className="max-w-4xl mx-auto">
              <Card 
                radius="none" 
                shadow="none"
                className="mb-6 border-0"
              >
                <CardHeader className="pb-4">
                  <div className="flex-1">
                    <h1 className="text-3xl font-bold text-text-primary">Main Dashboard</h1>
                    <p className="text-text-secondary mt-1">{Brand.tagline}</p>
                  </div>
                </CardHeader>
              </Card>
              <div className="flex flex-col min-h-full">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1 mb-6">
                  {/* Trading Hub Card - Opens session modal */}
                  <Card
                    isPressable
                    isHoverable
                    isFooterBlurred
                    onPress={handleOpenSessionModal}
                    className="h-75 hover:scale-105 transition-transform cursor-pointer"
                  >
                    <CardHeader className="absolute z-10 top-1 flex-col items-start!">
                      <h2 className="text-text-primary font-bold text-xl">Trading Hub</h2>
                    </CardHeader>
                    <Image
                      removeWrapper
                      alt="Trading Hub background"
                      className="z-0 w-4/5 h-4/5 object-contain absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                      src={TradingHubIllustrationPng}
                    />
                    <div className="absolute z-5 inset-0 bg-linear-to-br from-primary-400/10 to-secondary-400/10" />
                    <CardFooter className="justify-between border-0 overflow-hidden py-1 absolute before:rounded-xl rounded-large bottom-1 w-[calc(100%-8px)] shadow-small ml-1 z-10">
                      <p className="text-text-secondary text-sm">
                        Start trading sessions, capture real-time decisions, and document your trading psychology.
                      </p>
                    </CardFooter>
                  </Card>

                  {/* Analysis Hub Card */}
                  <Card 
                    isPressable
                    isHoverable
                    isFooterBlurred
                    onPress={handleNavigateToAnalysisHub}
                    className="h-75 hover:scale-105 transition-transform cursor-pointer"
                  >
                    <CardHeader className="absolute z-10 top-1 flex-col items-end!">
                      <h2 className="text-text-primary font-bold text-xl center">Analysis Hub</h2>
                    </CardHeader>
                    <Image
                      removeWrapper
                      alt="Analysis Hub background"
                      className="z-0 w-4/5 h-4/5 object-contain absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                      src={AnalysisHubIllustrationPng}
                    />
                    <div className="absolute z-5 inset-0 bg-linear-to-bl from-primary-400/10 to-secondary-400/10" />
                    <CardFooter className="justify-between bg-linear-to-br  overflow-hidden py-1 absolute before:rounded-xl rounded-large bottom-1 w-[calc(100%-8px)] shadow-small ml-1 z-10">
                      <p className="text-text-secondary text-sm">
                        Review trading performance, analyze behavioral patterns, and gain insights from your data.
                      </p>
                    </CardFooter>
                  </Card>
                </div>

                {/* Settings Card - Pinned to bottom */}
                <div className="mt-auto">
                  <Card 
                    isPressable
                    isHoverable
                    onPress={handleNavigateToSettings}
                    className="hover:scale-[1.02] transition-transform cursor-pointer max-w-md mx-auto"
                  >
                    <CardBody className="flex flex-row items-center gap-4 p-4 bg-linear-to-b from-secondary-400/10 to-warning-200/10">
                      <div>
                        <Settings size={24} className="text-text-secondary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-text-Primary">Settings</h3>
                        <p className="text-sm text-text-secondary">
                          Configure trading preferences and app settings
                        </p>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </div>
          </div>
        </ScrollShadow>
      </div>

      {/* Session Modal - Entry point for Trading Hub */}
      <SessionModal
        isOpen={isSessionModalOpen}
        onOpenChange={handleCloseSessionModal}
        drafts={drafts}
        sessionTypeOptions={['live', 'paper', 'observation', 'replay']}
        onStartNew={handleStartNew}
        onResume={handleResume}
        onDelete={handleDelete}
        todayDateString={todayDateString}
        displayTimezone={displayTimezone}
      />

      {/* Onboarding Reminder Modal */}
      {user && !isCheckingOnboarding && (
        <OnboardingReminderModal
          isOpen={showOnboardingReminder}
          userId={user.id}
          onClose={handleCloseOnboardingReminder}
        />
      )}
    </AppLayout>
  );
};